import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Settings,
  Save,
  ChevronRight,
  Globe,
  AlertTriangle,
  RefreshCw
} from 'lucide-react';
import { usePermissions } from '../../utils/permissionUtils';
import { systemSettingsAPI } from '../../services/api.js';

const SystemConfig = () => {
  const [activeTab, setActiveTab] = useState('general');
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Check permissions

  // General Platform Settings
  const [generalSettings, setGeneralSettings] = useState({
    platformName: 'Tokenised Bond Pilot Platform',
    contactEmail: 'admin@taas.gov.my',
    supportEmail: 'support@taas.gov.my',
    timezone: 'Asia/Kuala_Lumpur'
  });

  // Load settings from API on component mount
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const response = await systemSettingsAPI.getSettings();
        if (response.data.success) {
          setGeneralSettings({
            platformName: response.data.data.platformName,
            contactEmail: response.data.data.contactEmail,
            supportEmail: response.data.data.supportEmail,
            timezone: response.data.data.timezone
          });
        }
      } catch (error) {
        console.error('Failed to load system settings:', error);
      }
    };

    loadSettings();
  }, []);



  const tabs = [
    {
      id: 'general',
      name: 'General Settings',
      icon: Settings,
      description: 'Platform-wide settings and preferences'
    }
  ];

  // Page access is controlled by route permissions - no action-level check needed
  if (false) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h1>
          <p className="text-gray-600">You don't have permission to access this page.</p>
        </div>
      </div>
    );
  }

  const handleGeneralChange = (field, value) => {
    setGeneralSettings(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };



  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await systemSettingsAPI.updateSettings({
        platformName: generalSettings.platformName,
        contactEmail: generalSettings.contactEmail,
        supportEmail: generalSettings.supportEmail,
        timezone: generalSettings.timezone
      });
      
      if (response.data.success) {
        setHasChanges(false);
        // Show success notification
        alert('Settings saved successfully!');
      }
    } catch (error) {
      console.error('Failed to save settings:', error);
      // Show error notification
      alert('Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const renderGeneralSettings = () => (
    <div className="space-y-6">
      {/* Platform Information */}
      <div className="bg-white rounded-lg border border-light-gray">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900 flex items-center">
            <Globe className="h-5 w-5 mr-2" />
            Platform Information
          </h3>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Platform Name
              </label>
              <input
                type="text"
                value={generalSettings.platformName}
                onChange={(e) => handleGeneralChange('platformName', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Contact Email
              </label>
              <input
                type="email"
                value={generalSettings.contactEmail}
                onChange={(e) => handleGeneralChange('contactEmail', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Support Email
              </label>
              <input
                type="email"
                value={generalSettings.supportEmail}
                onChange={(e) => handleGeneralChange('supportEmail', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Timezone
              </label>
              <select
                value={generalSettings.timezone}
                onChange={(e) => handleGeneralChange('timezone', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="Asia/Kuala_Lumpur">Asia/Kuala_Lumpur</option>
                <option value="Asia/Singapore">Asia/Singapore</option>
                <option value="UTC">UTC</option>
              </select>
            </div>
          </div>
        </div>
      </div>






    </div>
  );



  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">System Configuration</h1>
              <p className="text-sm text-gray-500 mt-1">Configure platform settings, integrations, and system parameters</p>
            </div>
            {hasChanges && (
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors disabled:opacity-50"
              >
                {saving ? (
                  <RefreshCw className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                <span>{saving ? 'Saving...' : 'Save Changes'}</span>
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

      {/* Configuration Tabs */}
      <div className="bg-white border-light-gray rounded-lg border overflow-hidden">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6" aria-label="Tabs">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === tab.id
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                >
                  <Icon className="h-5 w-5 mr-2" />
                  {tab.name}
                </button>
              );
            })}
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'general' && renderGeneralSettings()}
        </div>
      </div>

      {/* Warning Banner */}
      {hasChanges && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
          <div className="flex">
            <AlertTriangle className="h-5 w-5 text-yellow-400 mr-3 mt-0.5" />
            <div>
              <h3 className="text-sm font-medium text-yellow-800">Unsaved Changes</h3>
              <p className="text-sm text-yellow-700 mt-1">
                You have unsaved configuration changes. Don't forget to save your changes before leaving this page.
              </p>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default SystemConfig; 